import { useEffect, useMemo, useState } from 'react'
import { CheckCircle2, Printer, QrCode, TriangleAlert, X } from 'lucide-react'
import {
  Bidi, Card, CardHeader, EmptyState, ErrorState, LoadingState, NoPermissionState, useLocale,
} from '@hk/ui'
import {
  fetchQrCodes, fetchQrSheetPdf, formatCount, issueMissingQrCodes, issueQrCode, may,
  revokeQrCode, translateError,
  type QrCodeBoard, type QrCodeView, type RoomWithoutCode,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-136 · ROOM QR CODES — the cards in the rooms.

   Built to pack 5. The backend has existed since Sprint 20 and the printed sheet has been
   proved by decoding it with a decoder that is not ours; this is the screen that lets
   somebody other than a developer run it.

   THE WORK IS PAPER, AND THE SCREEN IS SHAPED AROUND THAT. Somebody prints a sheet, cuts
   it up and walks a floor. So the rows are GROUPED BY FLOOR — five hundred in one table
   is a list nobody can find a room in — and the two buttons that matter, "issue the
   missing" and "print the cards", sit at the top where they are found before the table is
   read rather than after.

   FOUR FIGURES, BECAUSE "ARE WE READY TO PRINT?" IS A QUESTION ABOUT THE SET.
   Rooms, cards live, never issued, revoked. A per-room list cannot answer it: the rooms
   with no card are precisely the rows that would not be there.

   A REVOKED ROW STAYS, ABOVE ITS REPLACEMENT, AND SAYS WHY. The same room appears twice
   on purpose. Revoking does not delete a code — it ends it — so the requests that arrived
   through a damaged card are still attributable to the card that was on the door at the
   time, and a row that simply vanished would take that history with it.

   "REPRINT" IS NOT "REISSUE", and the screen must never let those read alike. Reprinting
   is the same code on new paper because the card was torn; reissuing is a NEW code
   because the old one was misused, and it costs a walk upstairs.
   ========================================================================= */

export function QrCodes({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [board, setBoard] = useState<QrCodeBoard | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [reload, setReload] = useState(0)
  const [showRevoked, setShowRevoked] = useState(false)
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState<'issuing' | 'printing' | null>(null)
  const [revoking, setRevoking] = useState<QrCodeView | null>(null)
  const [said, setSaid] = useState<string | null>(null)

  if (!may('admin.qrCodes')) return <NoPermissionState />

  useEffect(() => {
    let live = true
    setError(null)
    /* THE REVOKED ONES ARE ALWAYS FETCHED, and the checkbox only decides what is DRAWN.
       They are needed for the "revoked" figure whether or not the rows are shown, and one
       number that changes when a checkbox is ticked is a number nobody trusts. */
    fetchQrCodes(propertyId, true)
      .then((next) => { if (live) setBoard(next) })
      .catch((caught: unknown) => { if (live) setError(caught) })
    return () => { live = false }
  }, [propertyId, reload])

  /* ONE LIST OF TWO KINDS OF THING, and they stay told apart all the way down.

     A card and a room-with-no-card are different rows with different buttons, and the
     screen never pretends otherwise — but they belong in ONE table, sorted together by
     room number, because somebody walking a floor with a stack of cards reads the floor
     in door order and does not care which of the two a row happens to be. */
  const floors = useMemo(() => {
    if (!board) return []
    const needle = search.trim().toLowerCase()
    const matches = (roomNumber: string, floor: string) =>
      needle === ''
      || roomNumber.toLowerCase().includes(needle)
      || floor.toLowerCase().includes(needle)

    const rows: Line[] = [
      ...board.codes
        .filter((c) => (showRevoked || c.revokedAtUtc === null) && matches(c.roomNumber, c.floor))
        .map((code): Line => ({ kind: 'code', code })),
      ...board.roomsWithoutCode
        .filter((r) => matches(r.roomNumber, r.floor))
        .map((room): Line => ({ kind: 'room', room })),
    ]

    const byFloor = new Map<string, Line[]>()
    for (const row of rows) {
      const floor = row.kind === 'code' ? row.code.floor : row.room.floor
      const list = byFloor.get(floor)
      if (list) list.push(row)
      else byFloor.set(floor, [row])
    }

    for (const list of byFloor.values()) {
      /* IN DOOR ORDER, and a REVOKED card sits directly above the one that replaced it —
         which is what makes the same room appearing twice read as a history rather than
         as a duplicate. Ordinal on the room number, then oldest first within the room. */
      list.sort((a, b) => {
        const an = a.kind === 'code' ? a.code.roomNumber : a.room.roomNumber
        const bn = b.kind === 'code' ? b.code.roomNumber : b.room.roomNumber
        if (an !== bn) return an < bn ? -1 : 1
        const at = a.kind === 'code' ? a.code.issuedAtUtc : ''
        const bt = b.kind === 'code' ? b.code.issuedAtUtc : ''
        return at < bt ? -1 : at > bt ? 1 : 0
      })
    }

    return [...byFloor.entries()].sort((a, b) => floorOrder(a[0], b[0]))
  }, [board, search, showRevoked])

  async function run(what: 'issuing' | 'printing') {
    setBusy(what); setError(null)
    try {
      if (what === 'issuing') {
        const { issued } = await issueMissingQrCodes(propertyId)
        setSaid(issued === 0
          ? t.qrNothingMissing
          : t.qrIssuedCount.replace('{count}', formatCount(issued, locale)))
        setReload((n) => n + 1)
      } else {
        save(await fetchQrSheetPdf(propertyId), `room-cards-${propertyId}.pdf`)
      }
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(null)
    }
  }

  async function issueOne(room: RoomWithoutCode) {
    setError(null)
    try {
      await issueQrCode(propertyId, room.roomId)
      setSaid(t.qrIssued1.replace('{roomNumber}', room.roomNumber))
      setReload((n) => n + 1)
    } catch (caught: unknown) {
      setError(caught)
    }
  }

  if (error !== null && board === null) {
    return (
      <ErrorState
        body={translateError(error, locale).message}
        onRetry={() => setReload((n) => n + 1)}
      />
    )
  }
  if (!board) return <LoadingState />

  const summary = board.summary
  const requests = board.codes.reduce((total, code) => total + code.requestCount, 0)

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        {/* NOT THE SCREEN'S OWN NAME. The shell's header already says "Room QR codes"
            directly above this, and a card repeating it reads as a mistake — which it was,
            twice before this: the sidebar's "Reports" over "Reports" and the room master's
            "The hotel's rooms" over "The hotel's rooms". A heading names what is UNDER it. */}
        <CardHeader title={t.qrWhereTheyStand} icon={<QrCode className="size-4" />} />
        <div className="flex flex-col gap-3 p-3.5">
          <p className="max-w-[68ch] text-[12.5px] leading-relaxed text-muted-foreground">
            {t.qrIntro}
          </p>

          <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-5">
            <Figure label={t.qrRooms} value={formatCount(summary.rooms, locale)} />
            <Figure label={t.qrLive} value={formatCount(summary.withLiveCode, locale)} />
            {/* THE ONE THAT IS NOT A CONGRATULATION. A room with no card is a guest who
                telephones the desk, and it is invisible on every other screen. */}
            <Figure
              label={t.qrNever}
              value={formatCount(summary.withoutCode, locale)}
              tone={summary.withoutCode > 0 ? 'warn' : 'plain'}
            />
            <Figure label={t.qrRevoked} value={formatCount(summary.revoked, locale)} />
            <Figure label={t.qrRequests} value={formatCount(requests, locale)} />
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {summary.withoutCode > 0 && (
              <button
                type="button"
                onClick={() => void run('issuing')}
                disabled={busy !== null}
                className="inline-flex items-center gap-1.5 rounded-hk bg-[var(--hk-primary)]
                           px-3 py-1.5 text-xs font-medium text-white
                           disabled:opacity-60 cursor-pointer"
              >
                <QrCode className="size-3.5" />
                {busy === 'issuing'
                  ? t.qrIssuing
                  : t.qrIssueMissing.replace('{count}', formatCount(summary.withoutCode, locale))}
              </button>
            )}
            <button
              type="button"
              onClick={() => void run('printing')}
              disabled={busy !== null || summary.withLiveCode === 0}
              className="inline-flex items-center gap-1.5 rounded-hk border border-border
                         px-3 py-1.5 text-xs font-medium hover:bg-muted
                         disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
            >
              <Printer className="size-3.5" />
              {busy === 'printing' ? t.qrPrinting : t.qrPrint}
            </button>
            <span className="text-[11px] text-muted-foreground">{t.qrPrintHint}</span>

            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.qrSearch}
              aria-label={t.qrSearch}
              className="ms-auto h-8 w-44 rounded-hk border border-input bg-background px-2.5 text-[12.5px]"
            />
            {summary.revoked > 0 && (
              <label className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={showRevoked}
                  onChange={(e) => setShowRevoked(e.target.checked)}
                  className="size-3.5 cursor-pointer"
                />
                {t.qrShowRevoked}
              </label>
            )}
          </div>
        </div>
      </Card>

      {said !== null && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-hk border border-[#86efac] bg-[#f0fdf4]
                     px-3.5 py-3 text-[12.5px] text-[#166534]
                     dark:border-[#166534] dark:bg-[#0d2417] dark:text-[#86efac]"
        >
          <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
          <span className="flex-1"><Bidi>{said}</Bidi></span>
          <button type="button" onClick={() => setSaid(null)} className="cursor-pointer" aria-label="close">
            <X className="size-3.5" />
          </button>
        </div>
      )}

      {error !== null && (
        <ErrorState body={translateError(error, locale).message} onRetry={() => setError(null)} />
      )}

      {revoking && (
        <RevokeForm
          code={revoking}
          locale={locale}
          t={t}
          onCancel={() => setRevoking(null)}
          onDone={() => { setRevoking(null); setSaid(t.qrRevoked1); setReload((n) => n + 1) }}
        />
      )}

      {floors.length === 0
        ? (
          <Card>
            <div className="p-3.5">
              <EmptyState
                title={summary.rooms === 0 ? t.qrEmpty : t.qrNoMatch}
                body={summary.rooms === 0 ? '' : t.qrSearch}
              />
            </div>
          </Card>
        )
        : floors.map(([floor, codes]) => (
          <Card key={floor}>
            <CardHeader
              title={t.qrFloor.replace('{floor}', floor)}
              icon={<QrCode className="size-4" />}
            />
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr className="border-b border-border text-[11px] text-muted-foreground">
                  <th className="p-2 text-start font-medium">{t.qrColRoom}</th>
                  <th className="p-2 text-start font-medium">{t.qrColIssued}</th>
                  <th className="p-2 text-start font-medium">{t.qrColRequests}</th>
                  <th className="p-2 text-start font-medium">{t.qrColState}</th>
                  <th className="p-2 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {codes.map((line) => (line.kind === 'code'
                  ? (
                    <CodeRow
                      key={line.code.qrCodeId}
                      code={line.code}
                      locale={locale}
                      t={t}
                      onRevoke={() => setRevoking(line.code)}
                    />
                  )
                  : (
                    <NoCardRow
                      key={`no-card-${line.room.roomId}`}
                      room={line.room}
                      t={t}
                      onIssue={() => void issueOne(line.room)}
                    />
                  )))}
              </tbody>
            </table>
          </Card>
        ))}
    </div>
  )
}

/* ── the two kinds of row ─────────────────────────────────────────────────── */

/**
 * ONE LINE ON A FLOOR: either a code, or a room that has not got one.
 *
 * A discriminated union rather than one shape with optional fields, because the two draw
 * differently, offer different buttons and mean different things — and an optional-field
 * row is one where "no issue date" and "issued, date missing" are the same value.
 */
type Line =
  | { kind: 'code'; code: QrCodeView }
  | { kind: 'room'; room: RoomWithoutCode }

function CodeRow({
  code, locale, t, onRevoke,
}: {
  code: QrCodeView
  locale: 'en' | 'ar'
  t: typeof STRINGS['en']
  onRevoke: () => void
}) {
  const revoked = code.revokedAtUtc !== null

  return (
    <tr className={`border-b border-border last:border-0 ${revoked ? 'bg-muted/40' : ''}`}>
      {/* AN IDENTIFIER: Western digits in both languages. Painted on the door. */}
      <td className="p-2 font-mono font-semibold">{code.roomNumber}</td>
      <td className="p-2 text-muted-foreground">{localDate(code.issuedAtUtc, locale)}</td>
      {/* A QUANTITY, so it localises — ٣١ in Arabic, beside a room number that does not. */}
      <td className="p-2 tabular-nums">{formatCount(code.requestCount, locale)}</td>
      <td className="p-2">
        {revoked
          ? (
            /* THE REASON IS THE ROW. Without it a dead code is just clutter; with it, six
               weeks later, somebody can tell "this card was abused" from "we renumbered
               the floor" — which is the whole argument for keeping the row at all. */
            <span className="text-[11px] text-[#92400e] dark:text-[#fcd34d]">
              <Bidi>{t.qrSuperseded.replace('{reason}', code.revokeReason ?? '')}</Bidi>
            </span>
          )
          : (
            <span className="rounded-full bg-[#dcfce7] px-2 py-0.5 text-[11px] text-[#166534]
                             dark:bg-[#0d2417] dark:text-[#86efac]">{t.qrStateLive}</span>
          )}
      </td>
      <td className="p-2 text-end whitespace-nowrap">
        {!revoked && (
          <button
            type="button"
            onClick={onRevoke}
            className="rounded-hk border border-border px-2 py-1 text-[11px] hover:bg-muted cursor-pointer"
          >
            {t.qrRevoke}
          </button>
        )}
      </td>
    </tr>
  )
}

function NoCardRow({
  room, t, onIssue,
}: { room: RoomWithoutCode; t: typeof STRINGS['en']; onIssue: () => void }) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="p-2 font-mono font-semibold">{room.roomNumber}</td>
      {/* AN EM DASH, NOT A ZERO AND NOT A BLANK. There is no issue date and no request
          count, and drawing 0 would say the card exists and has been ignored. */}
      <td className="p-2 text-muted-foreground">—</td>
      <td className="p-2 text-muted-foreground">—</td>
      <td className="p-2">
        <span className="rounded-full bg-[#fef3c7] px-2 py-0.5 text-[11px] text-[#92400e]
                         dark:bg-[#2a1f07] dark:text-[#fcd34d]">{t.qrStateNever}</span>
      </td>
      <td className="p-2 text-end whitespace-nowrap">
        <button
          type="button"
          onClick={onIssue}
          className="rounded-hk border border-border px-2 py-1 text-[11px] hover:bg-muted cursor-pointer"
        >
          {t.qrIssue}
        </button>
      </td>
    </tr>
  )
}

/**
 * FLOORS SORT AS NUMBERS WHERE THEY ARE NUMBERS — the same rule the room loader applies,
 * for the same reason: a text sort puts 10 between 1 and 2, and this screen is read by
 * somebody walking the building in floor order.
 */
function floorOrder(a: string, b: string): number {
  const an = Number(a)
  const bn = Number(b)
  const aNumeric = a.trim() !== '' && Number.isFinite(an)
  const bNumeric = b.trim() !== '' && Number.isFinite(bn)
  if (aNumeric && bNumeric) return an - bn
  if (aNumeric !== bNumeric) return aNumeric ? -1 : 1
  return a < b ? -1 : a > b ? 1 : 0
}

/* ── revoking ─────────────────────────────────────────────────────────────── */

function RevokeForm({
  code, locale, t, onCancel, onDone,
}: {
  code: QrCodeView
  locale: 'en' | 'ar'
  t: typeof STRINGS['en']
  onCancel: () => void
  onDone: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function submit() {
    setBusy(true); setError(null)
    try {
      await revokeQrCode(code.qrCodeId, reason)
      onDone()
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader
        title={`${t.qrRevoke} · ${code.roomNumber}`}
        icon={<TriangleAlert className="size-4 text-[#b45309]" />}
      />
      <form
        onSubmit={(e) => { e.preventDefault(); if (reason.trim() !== '' && !busy) void submit() }}
        className="flex flex-col gap-3 p-3.5"
      >
        <label className="flex flex-col gap-1 text-[12px]">
          <span className="font-medium">{t.qrRevokeReason}</span>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label={t.qrRevokeReason}
            className="h-9 w-full rounded-hk border border-input bg-background px-2.5 text-[13px]"
          />
          <span className="max-w-[62ch] text-[11px] leading-snug text-muted-foreground">
            {t.qrRevokeHint}
          </span>
        </label>

        {error !== null && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-hk border border-[#fca5a5] bg-[#fef2f2]
                       px-3 py-2 text-[12.5px] text-[#991b1b]
                       dark:border-[#7f1d1d] dark:bg-[#2a0f0f] dark:text-[#fca5a5]"
          >
            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
            <span><Bidi>{translateError(error, locale).message}</Bidi></span>
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* SHUT UNTIL THERE IS A SENTENCE IN THE BOX. The server refuses a blank reason
              too — this is the courtesy of not letting somebody press a dead button. */}
          <button
            type="submit"
            disabled={reason.trim() === '' || busy}
            className="rounded-hk bg-[var(--hk-primary)] px-3.5 py-2 text-xs font-medium text-white
                       disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed
                       cursor-pointer"
          >
            {t.qrRevokeConfirm}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-hk border border-border px-3.5 py-2 text-xs font-medium
                       hover:bg-muted cursor-pointer"
          >
            {t.editCancel}
          </button>
        </div>
      </form>
    </Card>
  )
}

function Figure({
  label, value, tone = 'plain',
}: { label: string; value: string; tone?: 'plain' | 'warn' }) {
  return (
    <div
      className={`rounded-hk border p-2.5
                  ${tone === 'warn'
                    ? 'border-[#fcd34d] bg-[#fffbeb] dark:border-[#92400e] dark:bg-[#2a1f07]'
                    : 'border-border'}`}
    >
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-[19px] font-semibold tabular-nums">{value}</div>
    </div>
  )
}

function localDate(iso: string, locale: 'en' | 'ar'): string {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-IQ' : 'en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  }).format(new Date(iso))
}

/**
 * The PDF, saved.
 *
 * The same path W-110's report download takes: a blob and an object URL, because the
 * download needs the `Authorization` header and a browser sends none on a plain
 * navigation. **Revoked on the next turn of the loop** — immediately is too early, and
 * never holds the whole sheet in memory for the life of the page.
 */
function save(pdf: Blob, fileName: string): void {
  const url = URL.createObjectURL(pdf)
  try {
    const link = window.document.createElement('a')
    link.href = url
    link.download = fileName
    link.rel = 'noopener'
    window.document.body.appendChild(link)
    link.click()
    link.remove()
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}
